import React from "react";
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  IconButton,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Slider,
  Tooltip,
} from "@mui/material";
import Grid from "../utils/CompatGrid";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import FlipToFrontIcon from "@mui/icons-material/FlipToFront";
import FlipToBackIcon from "@mui/icons-material/FlipToBack";
import { BARCODE_TYPES } from "../utils/labelSizes";
import { normalizeNutritionRows } from "../utils/nutritionTable";

const FONT_FAMILIES = [
  "Poppins",
  "Noto Sans",
  "Noto Sans Devanagari",
  "Mukta",
  "Hind",
  "Roboto",
  "Arial",
];

const RightPanel = ({
  element,
  onChange,
  onDuplicate,
  onDelete,
  onBringForward,
  onSendBackward,
}) => {
  if (!element) {
    return (
      <Box sx={{ width: 280, p: 2, borderLeft: "1px solid #eee" }}>
        <Typography variant="body2" color="text.secondary">
          Select an element to edit its properties.
        </Typography>
      </Box>
    );
  }

  const set = (field, value) => onChange(element.id, { [field]: value });
  const nutritionRows =
    element.type === "nutrition"
      ? normalizeNutritionRows(element.nutritionRows)
      : [];

  return (
    <Box
      sx={{
        width: 280,
        p: 2,
        borderLeft: "1px solid #eee",
        height: "100%",
        overflowY: "auto",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Typography variant="subtitle2" fontWeight={700}>
          Properties
        </Typography>
        <Box>
          <Tooltip title="Duplicate">
            <IconButton size="small" onClick={() => onDuplicate(element.id)}>
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              color="error"
              onClick={() => onDelete(element.id)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
        <Tooltip title={element.hidden ? "Show" : "Hide"}>
          <IconButton
            size="small"
            onClick={() => set("hidden", !element.hidden)}
          >
            {element.hidden ? (
              <VisibilityOffIcon fontSize="small" />
            ) : (
              <VisibilityIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
        <Tooltip title={element.locked ? "Unlock" : "Lock"}>
          <IconButton
            size="small"
            onClick={() => set("locked", !element.locked)}
          >
            {element.locked ? (
              <LockIcon fontSize="small" />
            ) : (
              <LockOpenIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
        <Tooltip title="Bring Forward">
          <IconButton size="small" onClick={() => onBringForward(element.id)}>
            <FlipToFrontIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Send Backward">
          <IconButton size="small" onClick={() => onSendBackward(element.id)}>
            <FlipToBackIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Typography variant="caption" fontWeight={600}>
        Position &amp; Size (mm)
      </Typography>
      <Grid container spacing={1} sx={{ mb: 2 }}>
        <Grid item xs={6}>
          <TextField
            label="X"
            size="small"
            type="number"
            fullWidth
            value={element.x}
            onChange={(e) => set("x", Number(e.target.value))}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label="Y"
            size="small"
            type="number"
            fullWidth
            value={element.y}
            onChange={(e) => set("y", Number(e.target.value))}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label="Width"
            size="small"
            type="number"
            fullWidth
            value={element.width}
            onChange={(e) => set("width", Number(e.target.value))}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label="Height"
            size="small"
            type="number"
            fullWidth
            value={element.height}
            onChange={(e) => set("height", Number(e.target.value))}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="caption">
            Rotation ({element.rotation || 0}&deg;)
          </Typography>
          <Slider
            size="small"
            min={0}
            max={360}
            value={element.rotation || 0}
            onChange={(e, v) => set("rotation", v)}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="caption">Opacity</Typography>
          <Slider
            size="small"
            min={0}
            max={1}
            step={0.05}
            value={element.opacity != null ? element.opacity : 1}
            onChange={(e, v) => set("opacity", v)}
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 1 }} />

      {element.type === "text" && (
        <>
          <TextField
            label="Content (use {{placeholders}})"
            fullWidth
            multiline
            minRows={2}
            size="small"
            sx={{ mb: 2 }}
            value={element.content}
            onChange={(e) => set("content", e.target.value)}
          />
          <TextField
            select
            label="Font Family"
            size="small"
            fullWidth
            sx={{ mb: 2 }}
            value={element.fontFamily}
            onChange={(e) => set("fontFamily", e.target.value)}
          >
            {FONT_FAMILIES.map((f) => (
              <MenuItem key={f} value={f}>
                {f}
              </MenuItem>
            ))}
          </TextField>
          <Grid container spacing={1} sx={{ mb: 2 }}>
            <Grid item xs={6}>
              <TextField
                label="Font Size"
                type="number"
                size="small"
                fullWidth
                value={element.fontSize}
                onChange={(e) => set("fontSize", Number(e.target.value))}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Color"
                type="color"
                size="small"
                fullWidth
                value={element.color}
                onChange={(e) => set("color", e.target.value)}
              />
            </Grid>
          </Grid>
          <ToggleButtonGroup size="small" sx={{ mb: 2 }}>
            <ToggleButton
              value="bold"
              selected={element.bold}
              onClick={() => set("bold", !element.bold)}
            >
              <FormatBoldIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton
              value="italic"
              selected={element.italic}
              onClick={() => set("italic", !element.italic)}
            >
              <FormatItalicIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton
              value="underline"
              selected={element.underline}
              onClick={() => set("underline", !element.underline)}
            >
              <FormatUnderlinedIcon fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={element.textAlign}
            onChange={(e, v) => v && set("textAlign", v)}
            sx={{ mb: 2, ml: 1 }}
          >
            <ToggleButton value="left">
              <FormatAlignLeftIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value="center">
              <FormatAlignCenterIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value="right">
              <FormatAlignRightIcon fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <TextField
                label="Background"
                type="color"
                size="small"
                fullWidth
                value={
                  element.backgroundColor === "transparent"
                    ? "#ffffff"
                    : element.backgroundColor
                }
                onChange={(e) => set("backgroundColor", e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Padding"
                type="number"
                size="small"
                fullWidth
                value={element.padding || 0}
                onChange={(e) => set("padding", Number(e.target.value))}
              />
            </Grid>
          </Grid>
        </>
      )}

      {element.type === "image" && (
        <>
          <TextField
            label="Image URL / {{placeholder}}"
            fullWidth
            size="small"
            sx={{ mb: 2 }}
            value={element.src}
            onChange={(e) => set("src", e.target.value)}
            helperText="Use {{product_image}} or {{brand_logo}} for dynamic images"
          />
          <label style={{ display: "block", marginBottom: 16 }}>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                const reader = new FileReader();
                reader.onload = () => set("src", reader.result);
                reader.readAsDataURL(file);
              }}
            />
          </label>
        </>
      )}

      {element.type === "vegIcon" && (
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <TextField
              select
              label="Icon Source"
              size="small"
              fullWidth
              value={element.valueSource || "dynamic"}
              onChange={(e) => set("valueSource", e.target.value)}
              helperText="Auto uses the product Veg/Non-Veg value."
            >
              <MenuItem value="dynamic">Auto from Product</MenuItem>
              <MenuItem value="manual">Manual</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              select
              label="Icon Type"
              size="small"
              fullWidth
              value={element.manualValue || "VEG"}
              disabled={(element.valueSource || "dynamic") !== "manual"}
              onChange={(e) => set("manualValue", e.target.value)}
            >
              <MenuItem value="VEG">Veg</MenuItem>
              <MenuItem value="NON_VEG">Non-Veg</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      )}

      {element.type === "fssaiBadge" && (
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <TextField
              select
              label="Number Source"
              size="small"
              fullWidth
              value={element.valueSource || "dynamic"}
              onChange={(e) => set("valueSource", e.target.value)}
              helperText="Auto uses the product FSSAI number."
            >
              <MenuItem value="dynamic">Auto from Product</MenuItem>
              <MenuItem value="manual">Manual</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="FSSAI Number"
              size="small"
              fullWidth
              value={element.manualValue || ""}
              disabled={(element.valueSource || "dynamic") !== "manual"}
              onChange={(e) => set("manualValue", e.target.value)}
              helperText={
                (element.valueSource || "dynamic") === "manual"
                  ? "Enter the license number when no product is selected."
                  : "Switch to Manual if you want to type a number yourself."
              }
            />
          </Grid>
        </Grid>
      )}

      {(element.type === "rect" || element.type === "circle") && (
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <TextField
              label="Fill"
              type="color"
              size="small"
              fullWidth
              value={
                element.backgroundColor === "transparent"
                  ? "#ffffff"
                  : element.backgroundColor
              }
              onChange={(e) => set("backgroundColor", e.target.value)}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Border Color"
              type="color"
              size="small"
              fullWidth
              value={element.borderColor}
              onChange={(e) => set("borderColor", e.target.value)}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Border Width"
              type="number"
              size="small"
              fullWidth
              value={element.borderWidth}
              onChange={(e) => set("borderWidth", Number(e.target.value))}
            />
          </Grid>
          {element.type === "rect" && (
            <Grid item xs={6}>
              <TextField
                label="Corner Radius"
                type="number"
                size="small"
                fullWidth
                value={element.borderRadius || 0}
                onChange={(e) => set("borderRadius", Number(e.target.value))}
              />
            </Grid>
          )}
        </Grid>
      )}

      {element.type === "line" && (
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <TextField
              label="Color"
              type="color"
              size="small"
              fullWidth
              value={element.borderColor}
              onChange={(e) => set("borderColor", e.target.value)}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Thickness"
              type="number"
              size="small"
              fullWidth
              value={element.borderWidth}
              onChange={(e) => set("borderWidth", Number(e.target.value))}
            />
          </Grid>
        </Grid>
      )}

      {(element.type === "barcode" || element.type === "qr") && (
        <Grid container spacing={1}>
          {element.type === "barcode" && (
            <Grid item xs={12}>
              <TextField
                select
                label="Barcode Type"
                size="small"
                fullWidth
                value={element.barcodeType}
                onChange={(e) => set("barcodeType", e.target.value)}
              >
                {BARCODE_TYPES.filter((t) => t.value !== "QR").map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          )}
          <Grid item xs={12}>
            <TextField
              label="Value / {{placeholder}}"
              size="small"
              fullWidth
              value={element.barcodeValue}
              onChange={(e) => set("barcodeValue", e.target.value)}
            />
          </Grid>
        </Grid>
      )}

      {element.type === "nutrition" && (
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <TextField
              label="Title"
              size="small"
              fullWidth
              value={element.title || "Nutrition (per 100g)"}
              onChange={(e) => set("title", e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Value Header"
              size="small"
              fullWidth
              value={element.valueHeader || "Value"}
              onChange={(e) => set("valueHeader", e.target.value)}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Font Size"
              type="number"
              size="small"
              fullWidth
              value={element.fontSize}
              onChange={(e) => set("fontSize", Number(e.target.value))}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="caption" fontWeight={600}>
              Rows
            </Typography>
          </Grid>
          {nutritionRows.map((row, index) => (
            <React.Fragment key={row.id}>
              <Grid item xs={6}>
                <TextField
                  label={`Label ${index + 1}`}
                  size="small"
                  fullWidth
                  value={row.label}
                  onChange={(e) => {
                    const nextRows = nutritionRows.map((item) =>
                      item.id === row.id
                        ? { ...item, label: e.target.value }
                        : item,
                    );
                    set("nutritionRows", nextRows);
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={`Value ${index + 1}`}
                  size="small"
                  fullWidth
                  value={row.value}
                  onChange={(e) => {
                    const nextRows = nutritionRows.map((item) =>
                      item.id === row.id
                        ? { ...item, value: e.target.value }
                        : item,
                    );
                    set("nutritionRows", nextRows);
                  }}
                />
              </Grid>
            </React.Fragment>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default RightPanel;
